/**
 * BootstrapGate Component
 * Calls /api/access/login on app startup and routes based on access decision
 * Integrates with Static Web Apps Easy Auth (no token needed)
 */

import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import { checkLoginAccess } from '../services/bootstrapService';
import { getCurrentAccount } from '../services/authService';
import { getCurrentTenantId } from '../services/licenseService';
import { setUserContext, trackFeatureUsage, FeatureEvent, EventProp } from '../observability';

interface BootstrapGateProps {
    children: React.ReactNode;
}

// The Azure Functions backend runs on a plan that scales to zero when idle, so
// the first request after a break can time out purely from a cold start, not an
// actual outage. Auto-retry a few times with backoff (giving it up to ~30s on
// top of each request's own 15s timeout) before showing any error at all.
const MAX_AUTO_RETRIES = 3;
const RETRY_DELAYS_MS = [5000, 10000, 15000];
const BACKEND_UNAVAILABLE_PATTERN = /timed out|backend may not be running/i;

export function BootstrapGate({ children }: BootstrapGateProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const isAuthenticated = useIsAuthenticated();
    const [isChecking, setIsChecking] = useState(true);
    const [isWakingUp, setIsWakingUp] = useState(false);
    const [retryAttempt, setRetryAttempt] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const checkInProgressRef = useRef(false);
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Public routes that don't require access check
    // IMPORTANT: include all terminal error pages so BootstrapGate never re-routes away from them
    const publicRoutes = ['/platform', '/license-error', '/permissions-error', '/setup-required', '/onboarding', '/session-expired'];
    const isPublicRoute = location.pathname === '/' || publicRoutes.some(route => location.pathname.startsWith(route));

    useEffect(() => {
        console.log('[BootstrapGate] Effect running - pathname:', location.pathname, 'isPublicRoute:', isPublicRoute, 'isAuthenticated:', isAuthenticated, 'checkInProgress:', checkInProgressRef.current);

        // Skip check for public routes or if not authenticated
        if (isPublicRoute || !isAuthenticated) {
            console.log('[BootstrapGate] Skipping access check (public route or not authenticated)');
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
            setIsChecking(false);
            setIsWakingUp(false);
            setError(null); // Clear any previous errors
            checkInProgressRef.current = false;
            return;
        }

        // Prevent duplicate checks
        if (checkInProgressRef.current) {
            console.log('[BootstrapGate] Check already in progress, skipping');
            return;
        }

        // Perform login access check. On a "backend unavailable" style failure —
        // which covers both a real outage and an Azure Functions cold start after
        // the plan scaled to zero — auto-retry with backoff before surfacing any
        // error, so a normal cold start never shows the user anything scary.
        const attemptCheck = async (attempt: number) => {
            checkInProgressRef.current = true;
            try {
                console.log(`[BootstrapGate] Checking login access... (attempt ${attempt + 1})`);
                const response = await checkLoginAccess();

                console.log('[BootstrapGate] Access response:', response);
                setError(null);
                setIsWakingUp(false);

                const account = getCurrentAccount();
                const tenantId = getCurrentTenantId();
                if (account && tenantId) {
                    setUserContext({ userId: account.localAccountId, tenantId, userPrincipalName: account.username });
                }

                if (!response.allowed) {
                    // Route based on failure reason
                    console.log(`[BootstrapGate] Access denied - reason: ${response.reason}, route: ${response.route}`);
                    trackFeatureUsage(FeatureEvent.AccessDenied, { [EventProp.Reason]: response.reason, [EventProp.Route]: response.route });

                    // If setup is required, redirect to onboarding flow (not setup-required page)
                    if (response.route === 'setup-required') {
                        console.log('[BootstrapGate] Redirecting to /onboarding for wizard completion');
                        // Use setTimeout to prevent immediate re-trigger
                        setTimeout(() => navigate('/onboarding', { replace: true }), 100);
                    } else {
                        console.log(`[BootstrapGate] Redirecting to /${response.route}`);
                        setTimeout(() => navigate(`/${response.route}`, { replace: true, state: { reason: response.reason } }), 100);
                    }
                } else {
                    console.log('[BootstrapGate] Access granted, current path:', location.pathname);

                    // Only redirect to dashboard if the user landed on the root page.
                    // Do NOT redirect away from explicit error pages like /license-error
                    // or /permissions-error — those are terminal and must stay put.
                    if (location.pathname === '/') {
                        console.log('[BootstrapGate] Redirecting to dashboard');
                        setTimeout(() => navigate('/dashboard', { replace: true }), 100);
                    }
                    // Otherwise, let the user continue to their requested page
                }
            } catch (err: any) {
                const errorMsg = err.message || 'Failed to verify access';
                const isBackendUnavailable = BACKEND_UNAVAILABLE_PATTERN.test(errorMsg);

                if (isBackendUnavailable && attempt < MAX_AUTO_RETRIES) {
                    console.warn(`[BootstrapGate] Backend unavailable (attempt ${attempt + 1}/${MAX_AUTO_RETRIES}) — retrying in ${RETRY_DELAYS_MS[attempt]}ms`, err);
                    setIsWakingUp(true);
                    setRetryAttempt(attempt + 1);
                    retryTimerRef.current = setTimeout(() => attemptCheck(attempt + 1), RETRY_DELAYS_MS[attempt]);
                    return; // keep checkInProgressRef true — don't let another effect run race the pending retry
                }

                console.error('[BootstrapGate] Access check failed:', err);
                setError(errorMsg);
                setIsWakingUp(false);

                if (isBackendUnavailable) {
                    console.error('[BootstrapGate] Backend API still not responding after retries!');
                    // Don't redirect, just let the error show
                    setIsChecking(false);
                    checkInProgressRef.current = false;
                    return;
                }

                // On error, only redirect if not on a public route and not at /onboarding
                if (location.pathname !== '/' &&
                    !isPublicRoute &&
                    location.pathname !== '/onboarding') {
                    console.warn('[BootstrapGate] Error during check, redirecting to /onboarding');
                    navigate('/onboarding', { replace: true });
                }
                setIsChecking(false);
                checkInProgressRef.current = false;
                return;
            }
            setIsChecking(false);
            checkInProgressRef.current = false;
        };

        attemptCheck(0);

        return () => {
            // Cancel any pending retry AND release the in-progress guard — otherwise
            // a route/auth change mid-backoff would leave checkInProgressRef stuck
            // true forever, permanently blocking the next effect run's check.
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
            checkInProgressRef.current = false;
        };
    }, [isAuthenticated, location.pathname]); // Removed isPublicRoute and navigate from deps

    // Loading state - but NOT for public routes
    if (isChecking && !isPublicRoute && isAuthenticated) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
                <div className="text-center max-w-sm">
                    <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-purple-600 border-r-transparent"></div>
                    {isWakingUp ? (
                        <>
                            <p className="mt-4 text-lg font-semibold text-gray-700">Waking up your workspace…</p>
                            <p className="mt-2 text-sm text-gray-500">
                                This service pauses automatically when idle, so it can take a little longer to
                                start after a break. Retrying automatically ({retryAttempt}/{MAX_AUTO_RETRIES})…
                            </p>
                        </>
                    ) : (
                        <p className="mt-4 text-lg text-gray-700">Verifying access...</p>
                    )}
                </div>
            </div>
        );
    }

    // Error state - show error message (only reached after auto-retries are exhausted)
    if (error) {
        const isBackendUnavailable = BACKEND_UNAVAILABLE_PATTERN.test(error);
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
                <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
                    <div className={`flex items-center justify-center w-12 h-12 mx-auto rounded-full ${isBackendUnavailable ? 'bg-amber-100' : 'bg-red-100'}`}>
                        {isBackendUnavailable ? (
                            <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        ) : (
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        )}
                    </div>
                    <h3 className="mt-4 text-lg font-semibold text-center text-gray-900">
                        {isBackendUnavailable ? 'Still waking up…' : 'Connection Error'}
                    </h3>
                    <p className="mt-2 text-sm text-center text-gray-600">
                        {isBackendUnavailable
                            ? 'This can happen after a period of inactivity. Please wait a moment and try again. It usually only takes another try or two.'
                            : error}
                    </p>
                    {import.meta.env.DEV && error.includes('backend') && (
                        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
                            <p className="text-sm text-yellow-800">
                                <strong>Developer Note:</strong> Make sure the Azure Functions backend is running:
                            </p>
                            <code className="block mt-2 p-2 bg-gray-900 text-green-400 text-xs rounded">
                                cd src/api && npm start
                            </code>
                        </div>
                    )}
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-6 w-full px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // Legacy error state (non-blocking - show warning but allow through)
    if (false && !isPublicRoute) {
        console.warn('[BootstrapGate] Non-blocking error:', error);
    }

    return <>{children}</>;
}
